import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import FileSaver from "file-saver";

// Elements marked with this class are left out of every export
const EXPORT_IGNORE = ".mgw-export-ignore";

// Pseudo-classes and pseudo-elements a detached copy never matches, stripped
// before deciding whether a stylesheet rule is used by the export
const PSEUDO =
  /::?(hover|focus|focus-visible|focus-within|active|visited|before|after|placeholder)\b/g;

const fileName = (host, extension) => {
  const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
  return `mail-gateway-dashboard_${host}_${stamp}.${extension}`;
};

const pageBackground = (element) => {
  for (let node = element; node; node = node.parentElement) {
    const color = getComputedStyle(node).backgroundColor;
    if (color && color !== "transparent" && !/rgba\(.*,\s*0\)$/.test(color)) return color;
  }
  return "#ffffff";
};

/**
 * Copy of the dashboard for export: charts are turned into images (a cloned
 * canvas is blank), controls are dropped and the host select is replaced
 * with the host name and the time of the export.
 */
const snapshot = (element, host) => {
  const clone = element.cloneNode(true);

  const sourceCanvases = element.querySelectorAll("canvas");
  clone.querySelectorAll("canvas").forEach((canvas, index) => {
    const image = document.createElement("img");
    image.src = sourceCanvases[index].toDataURL("image/png");
    image.style.cssText = sourceCanvases[index].style.cssText;
    canvas.replaceWith(image);
  });

  clone.querySelectorAll(EXPORT_IGNORE).forEach((node) => node.remove());
  // Cards that shared a row with a removed card take the whole row
  clone.querySelectorAll(".mgw-export-full").forEach((node) => {
    node.style.gridColumn = "span 12";
  });

  const select = clone.querySelector(".mgw-dashboard__header__select");
  if (select) {
    const meta = document.createElement("p");
    meta.textContent = `Host: ${host} · Generated: ${new Date().toLocaleString()}`;
    select.replaceChildren(meta);
  }

  return clone;
};

const matchesSnapshot = (root, selectorText) => {
  if (/(^|[\s,>+~(])(html|body|:root)\b/.test(selectorText)) return true;
  try {
    const selector = selectorText.replace(PSEUDO, "");
    return root.matches(selector) || Boolean(root.querySelector(selector));
  } catch (e) {
    return false;
  }
};

// Relative url() references would break once the file is saved elsewhere
const absoluteUrls = (cssText, baseHref) =>
  cssText.replace(/url\((['"]?)(?!data:|https?:|#)([^'")]+)\1\)/g, (_, quote, path) => {
    return `url(${quote}${new URL(path, baseHref).href}${quote})`;
  });

// The stylesheet rules the snapshot uses, plus links to stylesheets the page
// cannot read (cross-origin fonts)
const collectStyles = (root) => {
  const rules = [];
  const links = [];
  Array.from(document.styleSheets).forEach((sheet) => {
    let cssRules;
    try {
      cssRules = sheet.cssRules;
    } catch (e) {
      if (sheet.href) links.push(`<link rel="stylesheet" href="${sheet.href}">`);
      return;
    }
    const baseHref = sheet.href || window.location.href;
    Array.from(cssRules).forEach((rule) => {
      if (rule.selectorText && !matchesSnapshot(root, rule.selectorText)) return;
      rules.push(absoluteUrls(rule.cssText, baseHref));
    });
  });
  return { css: rules.join("\n"), links: links.join("\n") };
};

export const exportDashboardHtml = (element, host) => {
  const clone = snapshot(element, host);
  const { css, links } = collectStyles(clone);
  const theme = document.documentElement.getAttribute("data-theme") || "dark";
  const fontFamily = getComputedStyle(document.body).fontFamily;

  const html = `<!doctype html>
<html lang="en" data-theme="${theme}">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Mail Gateway Dashboard - ${host}</title>
${links}
<style>
${css}
body { margin: 0; background: ${pageBackground(element)}; font-family: ${fontFamily}; }
</style>
</head>
<body>
${clone.outerHTML}
</body>
</html>`;

  FileSaver.saveAs(new Blob([html], { type: "text/html;charset=utf-8" }), fileName(host, "html"));
};

// One tall page the size of the dashboard, so no card is cut by a page break
export const exportDashboardPdf = async (element, host) => {
  const clone = snapshot(element, host);
  const container = document.createElement("div");
  container.style.cssText = `position: fixed; top: 0; left: -100000px; width: ${element.offsetWidth}px;`;
  container.appendChild(clone);
  document.body.appendChild(container);

  try {
    await Promise.all(
      Array.from(clone.querySelectorAll("img")).map((image) => image.decode().catch(() => {})),
    );
    // Scale 1.5 keeps a long dashboard under Safari's canvas size limit
    const canvas = await html2canvas(clone, {
      scale: 1.5,
      backgroundColor: pageBackground(element),
      windowWidth: element.offsetWidth,
      logging: false,
    });
    const width = canvas.width / 1.5;
    const height = canvas.height / 1.5;
    const pdf = new jsPDF({
      orientation: width > height ? "landscape" : "portrait",
      unit: "px",
      format: [width, height],
      hotfixes: ["px_scaling"],
    });
    pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, width, height);
    pdf.save(fileName(host, "pdf"));
  } finally {
    container.remove();
  }
};
